import { Injectable } from '@angular/core';
import { LandingConfigService } from '../registration-core/landing-config.service';

/**
 * Attribution data assembled from the landing configuration context.
 *
 * `is_landing` and `source` are always present. `source` is `partner-campaign`
 * for the landing page, otherwise the resolved clientId.
 */
export interface AttributionData {
  is_landing: boolean;
  source: string;
}

/**
 * Assembles Attribution_Data (`is_landing` and `source`) from
 * LandingConfigService.
 *
 * Design notes:
 * - `capture()` is safe — never throws; returns a minimal object on error.
 *
 * Not providedIn root — provided per embed instance.
 */
@Injectable()
export class AttributionService {
  private _attribution: AttributionData;

  constructor(private readonly landingConfig: LandingConfigService) {
    this._attribution = this.capture();
  }

  /**
   * Returns the captured attribution data.
   */
  getAttribution(): AttributionData {
    return this._attribution;
  }

  /**
   * Assembles AttributionData from the landing configuration.
   * Safe — never throws; returns minimal attribution on error.
   */
  capture(): AttributionData {
    try {
      this._attribution = {
        is_landing: this.landingConfig.isLanding,
        source: this.landingConfig.resolveSource(),
      };
      return this._attribution;
    } catch {
      // Never throw — return minimal attribution
      const fallback: AttributionData = {
        is_landing: this.landingConfig?.isLanding ?? true,
        source: 'partner-campaign',
      };
      this._attribution = fallback;
      return fallback;
    }
  }
}
